import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../store/authStore'

// Extend config to carry a retry flag so we never refresh twice on the same request
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
})

// ── Request interceptor — attach Bearer token ────────────────────────────────

api.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Shared token refresh — used by the response interceptor below, and by ────
// any raw-fetch caller (e.g. the SCADA SSE stream) that bypasses axios and
// needs to recover from a 401 on its own without racing a second refresh.

// Pages like Infrastruktura fire several authenticated requests in parallel on
// mount. If the token has expired, they all 401 at once — without sharing a
// single in-flight refresh, each would independently call /auth/refresh.
// This holds the one shared refresh promise so concurrent 401s await it
// instead of triggering redundant refresh calls.
let refreshPromise: Promise<{
  access_token: string
  refresh_token: string
  license_active: boolean
  license_expires_at: string | null
}> | null = null

/** Resolves to the fresh access token, or forces logout + redirect and throws. */
export async function refreshAccessToken(): Promise<string> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState()

  if (!refreshToken) {
    logout()
    window.location.href = '/login'
    throw new Error('No refresh token available')
  }

  try {
    if (!refreshPromise) {
      refreshPromise = axios
        .post('/api/v1/auth/refresh', { refresh_token: refreshToken })
        .then(({ data }) => data)
        .finally(() => { refreshPromise = null })
    }

    // The refresh token rotates on every call (docs/plan-security-hardening-1.md
    // §2) — store the new one, not the one we sent. license_active/
    // license_expires_at come fresh on every refresh too (plan-license-
    // expiry-read-only.md), so the banner never goes stale between logins.
    const { access_token, refresh_token, license_active, license_expires_at } = await refreshPromise
    setTokens(access_token, refresh_token, license_active, license_expires_at)
    return access_token
  } catch (e) {
    // Refresh failed — expired, or the session was revoked (logout
    // elsewhere, blocking, password change, reuse past the rotation grace
    // window). One attempt only, no retry: force logout with whatever
    // message the server gave, so the login screen explains why.
    const detail = axios.isAxiosError(e) ? (e.response?.data as { detail?: string } | undefined)?.detail : undefined
    if (detail) {
      try { sessionStorage.setItem('authNotice', detail) } catch { /* private browsing, storage disabled */ }
    }
    logout()
    window.location.href = '/login'
    throw e
  }
}

// ── Response interceptor — refresh on 401, logout on second failure ──────────

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined

    // docs/plan-license-expiry-read-only.md: the backend is the real
    // enforcement (423 on a write once the tenant's license has expired).
    // Button-disabling is cosmetic and not yet wired on every page, so
    // this is the one place that guarantees the message always surfaces,
    // whichever page the write came from.
    if (error.response?.status === 423) {
      const detail = (error.response.data as { detail?: string } | undefined)?.detail
      window.alert(detail ?? 'Licenca je istekla. Moguć je samo pregled i izvoz podataka.')
      return Promise.reject(error)
    }

    // Only attempt a refresh once per request, and only on 401
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true                    // guard against infinite loop

      try {
        const access_token = await refreshAccessToken()
        // Replay original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
      } catch {
        // refreshAccessToken() already forced logout + redirect
      }
    }

    return Promise.reject(error)
  },
)

export default api
